import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useLingui } from '@lingui/react/macro'
import { ArrowRightIcon, ArrowUpDownIcon } from 'lucide-react'
import { SparklesIcon } from '@/routes/_app/-components/shared/navigation-config'
import { trpc } from '@/main'
import { useAppStore } from '@/lib/app-store'
import { useHydrated } from '@/hooks/use-hydrated'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Appear } from '@/components/custom/appear'
import { NAVIGATION_ICONS, type NavigationIcon } from '../shared/navigation-config'
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog'

// Per-entity icon tints, kept in sync with the section colors used across the app (CATEGORY_CONFIG in
// report/sections/profile-selector-popover.tsx and the avatar palette): jobs=blue, teams=violet,
// employees=emerald, offers=indigo. Static class strings so Tailwind keeps them.
const ENTITY_STYLE = {
  jobs: 'bg-blue-500/10 text-blue-500',
  teams: 'bg-violet-500/10 text-violet-500',
  employees: 'bg-emerald-500/10 text-emerald-500',
  offers: 'bg-indigo-500/10 text-indigo-500',
} as const

export function CompanyWelcomeDialog() {
  const { t } = useLingui()
  const navigate = useNavigate()
  const { data: user } = useSuspenseQuery(trpc.auth.getUser.queryOptions())
  const queryClient = useQueryClient()
  const hydrated = useHydrated()
  const welcomeDismissed = useAppStore((s) => s.welcomeDismissed)
  const dismissWelcome = useAppStore((s) => s.dismissWelcome)

  const { mutateAsync: dismiss } = useMutation(
    trpc.auth.dismissCompanyWelcome.mutationOptions({
      onSettled: () => queryClient.invalidateQueries(trpc.auth.getUser.queryFilter()),
    }),
  )

  const isCompany = user?.type === 'company'
  // `tour === 0` is the default for a freshly registered company (non-zero = permanently dismissed)
  const open = hydrated && isCompany && user.tour === 0 && !welcomeDismissed

  // On dismissal (either button, or the backdrop), send the company to the Jobs ("Puestos") section —
  // the recommended first step of the setup.
  const handleClose = () => {
    dismissWelcome()
    navigate({ to: '/jobs' })
  }

  const handleNeverShow = () => {
    dismissWelcome()
    dismiss()
    navigate({ to: '/jobs' })
  }

  // The three building blocks, in the recommended order — each card says what to do in that section.
  const steps: { icon: NavigationIcon; style: string; label: string; hint: string }[] = [
    {
      icon: NAVIGATION_ICONS.job,
      style: ENTITY_STYLE.jobs,
      label: t`Jobs`,
      hint: t`Create all the positions in your organization and define the ideal psychographic profile for each one.`,
    },
    {
      icon: NAVIGATION_ICONS.team,
      style: ENTITY_STYLE.teams,
      label: t`Teams`,
      hint: t`Add the existing teams in your company.`,
    },
    {
      icon: NAVIGATION_ICONS.employee,
      style: ENTITY_STYLE.employees,
      label: t`Employees`,
      hint: t`Add your employees and assign each one their corresponding position. If you wish, you can also add them to one or more teams. Start by adding yourself, using your employee email address.`,
    },
  ]

  return (
    <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
      <DialogContent
        showCloseButton={false}
        // Require an explicit choice (Close / Don't show again) — clicking the backdrop must not dismiss it.
        onInteractOutside={(e) => e.preventDefault()}
        className="scrollbar flex max-h-[calc(100dvh-2rem)] flex-col gap-0 p-0 sm:max-w-lg"
      >
        <div className="flex min-h-0 flex-col gap-7 overflow-y-auto px-6 py-8">
          <div className="text-center">
            <DialogTitle className="text-2xl font-semibold">{t`We welcome you to D'Anchiano`}</DialogTitle>
            <DialogDescription className="mt-1.5 text-base">
              {t`Set up your account in a clear and simple way`}
            </DialogDescription>
          </div>

          {/* GETTING STARTED — the three basics every organization needs, as numbered steps */}
          <section className="flex flex-col gap-3">
            <div className="flex flex-col gap-1 px-1">
              <p className="text-muted-foreground text-sm">
                {t`To get started, we need three basic elements of your organization. We recommend following this order:`}
              </p>
            </div>
            <ul className="flex flex-col gap-3">
              {steps.map((item, index) => {
                const Icon = item.icon
                return (
                  <Appear
                    as="li"
                    key={item.label}
                    delay={index * 120}
                    className="bg-card flex items-start gap-4 rounded-xl border p-4"
                  >
                    <div className="relative shrink-0">
                      <div className={cn('flex size-12 items-center justify-center rounded-xl', item.style)}>
                        <Icon className="size-6" />
                      </div>
                      <span className="bg-foreground text-background absolute -top-2 -left-2 flex size-6 items-center justify-center rounded-full text-xs font-semibold">
                        {index + 1}
                      </span>
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <span className="font-semibold">{item.label}</span>
                      <span className="text-muted-foreground text-sm">{item.hint}</span>
                    </div>
                  </Appear>
                )
              })}
            </ul>
          </section>

          {/* KEEP IN MIND — flexible order, what happens next, and the optional offers section */}
          <section className="flex flex-col gap-3">
            <span className="text-muted-foreground px-1 text-xs font-semibold tracking-wider uppercase">
              {t`Keep in mind`}
            </span>
            <ul className="flex flex-col gap-3">
              <Note icon={ArrowUpDownIcon} delay={steps.length * 120}>
                {t`This is the order we suggest, but you can use whatever order suits you best.`}
              </Note>
              <Note icon={SparklesIcon} title={t`When you finish...`} delay={(steps.length + 1) * 120}>
                <span>{t`Each employee will receive an invitation to complete their Psychographic Profile Test.`}</span>
                <span>{t`From that moment on, D'Anchiano will automatically start analyzing the information and let you generate valuable insights about the talent in your organization.`}</span>
                <span>{t`The more profiles are completed, the more value you will get.`}</span>
              </Note>
              <Note
                icon={NAVIGATION_ICONS.offer}
                iconStyle={ENTITY_STYLE.offers}
                title={t`Offers`}
                delay={(steps.length + 2) * 120}
              >
                {t`Optionally, you can also create offers and invite candidates to each of them.`}
              </Note>
            </ul>
          </section>
        </div>

        {/* CTA — sticky footer so the actions stay reachable while the body scrolls */}
        <div className="flex shrink-0 flex-col gap-2 px-6 py-4">
          <Button type="button" onClick={handleClose} className="h-12 w-full text-base font-semibold">
            {t`Close`}
            <ArrowRightIcon className="size-5" />
          </Button>
          <Button type="button" variant="ghost" onClick={handleNeverShow} className="h-12 w-full">
            {t`Don't show again`}
          </Button>
          <p className="text-muted-foreground text-center text-xs">
            {t`You can find it again in Settings > Welcome guide.`}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// One "keep in mind" card: icon box (neutral by default, or an entity tint via `iconStyle`) + title +
// one or more lines of body copy.
function Note(props: {
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>
  iconStyle?: string
  title?: string
  delay: number
  children: React.ReactNode
}) {
  const Icon = props.icon
  const iconStyle = props.iconStyle ?? 'bg-muted text-foreground/70'
  return (
    <Appear as="li" delay={props.delay} className="bg-card flex items-start gap-4 rounded-xl border p-4">
      <div className={cn('flex size-12 shrink-0 items-center justify-center rounded-xl', iconStyle)}>
        <Icon className="size-6" strokeWidth={1.5} />
      </div>
      <div className="flex flex-col gap-1">
        {props.title && <span className="font-semibold">{props.title}</span>}
        <span className="text-muted-foreground flex flex-col gap-1 text-sm">{props.children}</span>
      </div>
    </Appear>
  )
}
